import { c as _c } from "react/compiler-runtime";
import Joi from 'joi';
import { useCallback } from 'react';
import { useGlobalMeasures, useMeasureActions } from '@/hooks/store/use-measure-store';
import { useTranslation } from '@/hooks/use-translation';
import { DEFAULT_KEY_WEIGHT_RATIO, DEFAULT_WIPPEN_RADIUS_WEIGHT } from '@/lib/piano/touch-design/constants';
import type { OptionalNumber } from '@/lib/piano/touch-design/measured-key.requirements';
import { MeasureInputField } from './MeasureInputField';

/**
 * The two values that hold for the whole instrument.
 *
 * They sit on one line above the table rather than in a section of their own:
 * they are measured once, and the rest of the page is the real work.
 */
export const PianoMeasureList = () => {
  const $ = _c(35);
  const pianoGlobalMeasures = useGlobalMeasures();
  const {
    updateGlobalMeasure
  } = useMeasureActions();
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== updateGlobalMeasure) {
    t0 = value => {
      updateGlobalMeasure("keyWeightRatio", value);
    };
    $[0] = updateGlobalMeasure;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onUpdateKeyRatio = t0;
  let t1;
  if ($[2] !== updateGlobalMeasure) {
    t1 = value_0 => {
      updateGlobalMeasure("wippenRadiusWeight", value_0);
    };
    $[2] = updateGlobalMeasure;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onUpdateWippenRadiusWeight = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("For the whole piano");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("For the whole piano");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <h2 className="m-0 font-sans text-sm font-semibold">{t3}</h2>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("Key weight ratio");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = pianoGlobalMeasures.keyWeightRatio;
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = DEFAULT_KEY_WEIGHT_RATIO.toString();
    t8 = Joi.number().min(0.1).max(1).optional();
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  let t9;
  if ($[14] !== onUpdateKeyRatio || $[15] !== pianoGlobalMeasures.keyWeightRatio) {
    t9 = <MeasureInputField className="w-[72px]" defaultValue={t6} onUpdate={onUpdateKeyRatio} placeholder={t7} validator={t8} />;
    $[14] = onUpdateKeyRatio;
    $[15] = pianoGlobalMeasures.keyWeightRatio;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t5 || $[18] !== t9) {
    t10 = <label className="inline-flex items-center gap-2.5 text-[13.5px] text-muted-foreground">{t5}{t9}</label>;
    $[17] = t5;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t) {
    t11 = t("Wippen radius weight");
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = pianoGlobalMeasures.wippenRadiusWeight;
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = DEFAULT_WIPPEN_RADIUS_WEIGHT.toString();
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== onUpdateWippenRadiusWeight || $[24] !== pianoGlobalMeasures.wippenRadiusWeight) {
    t14 = <MeasureInputField className="w-[72px]" defaultValue={t12} onUpdate={onUpdateWippenRadiusWeight} placeholder={t13} />;
    $[23] = onUpdateWippenRadiusWeight;
    $[24] = pianoGlobalMeasures.wippenRadiusWeight;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="font-mono text-xs text-ptl-ink-3">g</span>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t11 || $[28] !== t14) {
    t16 = <label className="inline-flex items-center gap-2.5 text-[13.5px] text-muted-foreground">{t11}{t14}{t15}</label>;
    $[27] = t11;
    $[28] = t14;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t10 || $[31] !== t16 || $[32] !== t2 || $[33] !== t4) {
    t17 = <section aria-label={t2} className="flex flex-wrap items-center gap-x-7 gap-y-3 rounded-md border border-border bg-card px-4 py-3">{t4}{t10}{t16}</section>;
    $[30] = t10;
    $[31] = t16;
    $[32] = t2;
    $[33] = t4;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  return t17;
};